<script setup lang="ts">
import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    watch,
    type Ref,
} from 'vue';
import { useQuasar, QScrollArea, QHeader } from 'quasar';
import TestQuestion from '@/components/Student/TestReview/TestQuestion.vue';
import useQuestionPagination from '@/composables/useQuestionPagination';
import CoursePerformanceRepository from '@/repositories/CoursePerformanceRepository';
import { usePerformanceStore } from '@/stores/performance';
import { useSchoolStore } from '@/stores/school';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import type { ExamPerformanceTypes } from '@/types/Performance';
import { TestType, type Question, QuestionStatus } from '@/types/TestReview';
import TestQuestionList from '@/components/Student/TestReview/TestQuestionList.vue';
import TestReviewPerformance from '@/components/Student/TestReview/TestReviewPerformance.vue';
import {
    filterByQuestionStatuses, titleGenerators, getStudyUnitNumber, getPrimaryQuestionStatus,
} from '@/functions/testReviewHelpers';
import QuestionListDialog from '@/components/Student/TestReview/Dialogs/QuestionListDialog.vue';
import PerformanceDialog from '@/components/Student/TestReview/Dialogs/PerformanceDialog.vue';
import QuestionFilterDialog from '@/components/Student/TestReview/Dialogs/QuestionFilterDialog.vue';
import QuestionFilterMenu from '@/components/Student/TestReview/QuestionFilterMenu.vue';
import QuestionFilterButton from '@/components/Student/TestReview/QuestionFilterButton.vue';
import TestReviewStatusIcon from '@/components/Student/TestReview/TestReviewStatusIcon.vue';

const props = defineProps<{
    cusId: number;
    testId: number;
    testType: TestType;
}>();

const questions = ref<Question[]>([]);

const $q = useQuasar();
const scrollAreaRef = ref<typeof QScrollArea | null>(null);
const loading = ref<boolean>(true);
const menuOpen = ref<boolean>(false);

/**
 * Only show incorrect questions by default.
 * In onMounted, this is cleared if there are no incorrect questions.
 */
const selectedFilters = ref<string[]>([QuestionStatus.incorrect]);

const pagination = useQuestionPagination({
    min: 1,
    max: 1,
});

const selectedQuestion = ref<number>(1);

const updateQuestionVisibility = (questionsToUpdate: Question[]): Question[] => {
    if (selectedQuestion.value === 0) {
        // set all questions to visible
        return questionsToUpdate.map((question: Question) => (
            {
                ...question,
                visible: true,
            }
        ));
    }
    // set only the selected question to visible
    return questionsToUpdate.map((question: Question) => (
        {
            ...question,
            visible: question.number === selectedQuestion.value,
        }
    ));
};

const setSelectedQuestion = (questionNumber: number) => {
    selectedQuestion.value = questionNumber;
};

const updatePagination = (max: number) => {
    pagination.value = useQuestionPagination({
        min: 1,
        max,
    }).value;
    pagination.value.set(1);
};

const filteredQuestions = computed(() => {
    const newlyFilteredQuestions = filterByQuestionStatuses(questions.value, selectedFilters.value);
    if (newlyFilteredQuestions.length !== pagination.value.max) {
        updatePagination(newlyFilteredQuestions.length);
        if (newlyFilteredQuestions.length === 0) {
            return [];
        }

        setSelectedQuestion(newlyFilteredQuestions[0].number);
    }

    return updateQuestionVisibility(newlyFilteredQuestions);
});

/**
 * Auto update the scroll position of the question list to prevent the selected question being out of bounds
 *
 * Only update when previous/next buttons are pressed to prevent buggy scroll behavior.
 */
const updateScrollPosition = () => {
    document.querySelector('.question-list-item .q-radio__inner--truthy')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
    });
};

// when pagination changes, update the selected question
watch(() => pagination.value.page, (newVal: number) => {
    // page is different than the question number
    // get the index of the question from the page
    const questionIndex = newVal - 1;
    const question = filteredQuestions.value[questionIndex];
    if (!question) {
        return;
    }

    selectedQuestion.value = question.number;
    questions.value = updateQuestionVisibility(questions.value);
});

const student = ref<FlightSchoolCourseStudent | null>(null);

const exam = ref<ExamPerformanceTypes>(null);

const schoolStore = useSchoolStore();
const performanceStore = usePerformanceStore();

/** Used to ensure that content isn't covered by the header when the drawer is in mobile mode */
const qHeaderRef = ref<typeof QHeader | null>(null);
const computedHeaderHeight = computed(() => {
    const height = qHeaderRef.value?.$el.clientHeight ?? 0;
    return `${height}px`;
});

/**
 * Custom stuff for calculating the height of the performance and filter sections.
 * Needed because the scroll area for the question list needs to have a defined height.
 *
 * Some heights are hardcoded just in case the refs are not available.
 */
const normalFilterHeight = 60;
const normalExpandedPerformanceHeight = 265;
const normalCollapsedPerformanceHeight = 55;

const isPerformanceExpanded = ref<boolean>(true);
const performanceRef = ref<typeof TestReviewPerformance | null>(null);
const filterRef = ref<typeof QuestionFilterMenu | null>(null);

/** The height of the performance and filter sections in the QDrawer. */
const topSectionHeight = ref(normalExpandedPerformanceHeight + normalFilterHeight);

const updateTopSectionHeight = () => {
    const performanceFallbackHeight = isPerformanceExpanded.value
        ? normalExpandedPerformanceHeight : normalCollapsedPerformanceHeight;

    const filterHeight = filterRef.value?.$el.clientHeight ?? normalFilterHeight;
    const performanceHeight = performanceRef.value?.$el.clientHeight ?? performanceFallbackHeight;

    topSectionHeight.value = filterHeight + performanceHeight;
};

const updatePerformanceVisibility = (value: boolean) => {
    isPerformanceExpanded.value = value;
};

const examLoaders: Ref<Record<TestType, (id: number) => ExamPerformanceTypes>|null> = ref(null);

// this should probably be it's own component?
const loadExamQuestions = async () => {
    if (!schoolStore.school || !schoolStore.course || !student.value || !examLoaders.value) {
        return;
    }

    const loadExam = examLoaders.value[props.testType];
    if (loadExam) {
        exam.value = loadExam(props.testId);
    }

    if (!exam.value) {
        return;
    }

    questions.value = await CoursePerformanceRepository.getStudentTestSession(
        schoolStore.school,
        schoolStore.course,
        student.value,
        // temp, needs to be exam specific. This is a testSessionId
        props.testId,
    );

    pagination.value.max = questions.value.length;
};

const loadStudyQuestions = async () => {
    if (!schoolStore.school || !schoolStore.course || !student.value) {
        return;
    }

    exam.value = performanceStore.getStudyQuestionsPerformanceByStudyUnit(
        props.testId,
    );

    if (!exam.value) {
        return;
    }

    questions.value = await CoursePerformanceRepository.getStudentStudySession(
        schoolStore.school,
        schoolStore.course,
        student.value,
        exam.value.uuid,
    );

    pagination.value.max = questions.value.length;
};

const studyUnitNumber = ref<number | null>(null);

const testTitle = computed(() => {
    if (!exam.value) {
        return '';
    }

    return titleGenerators[props.testType](exam.value);
});

const getPageHeight = (offset: number, height: number): {height: string} => ({ height: `${height - offset}px` });

const isFilterDialogOpen = ref(false);
const openPerformanceDialog = () => $q.dialog({
    component: PerformanceDialog,
    componentProps: {
        questions: questions.value,
        testType: props.testType,
        school: schoolStore.school,
        course: schoolStore.course,
        student: student.value,
        exam: exam.value,
    },
});
const openQuestionListDialog = () => $q.dialog({
    component: QuestionListDialog,
    componentProps: {
        questionIndex: pagination.value.page - 1,
        questions: filteredQuestions.value,
    },
});

/**
 * The height of the quiz review container should take up as much space as possible on tablets and mobile devices.
 */
const usableHeight = ref('0px');
const updateUsableScreenHeight = () => {
    const backButtonHeight = document.querySelector('.back-button')?.clientHeight ?? 0;

    // Factor in the bottom padding that's applied to #fsum-main-panel
    const mainPanel = document.querySelector('#fsum-main-panel');

    if (!mainPanel) {
        // If the element is not found, use a default padding value of 24px
        usableHeight.value = `calc(${window.innerHeight - backButtonHeight - 24}px - var(--fsum-header-height))`;
        return;
    }

    const padding = Number.parseInt(window.getComputedStyle(mainPanel).getPropertyValue('padding-bottom'), 10);
    usableHeight.value = `calc(${window.innerHeight - backButtonHeight - padding}px - var(--fsum-header-height))`;
};

onMounted(async () => {
    $q.loading.show();

    if (schoolStore.students.length === 0) {
        await schoolStore.setStudents();
    }

    student.value = schoolStore.findStudent(props.cusId);

    if (!schoolStore.school || !schoolStore.course || !student.value) {
        $q.loading.hide();
        return;
    }

    if (performanceStore.performance === null) {
        performanceStore.setPerformance(
            await CoursePerformanceRepository.getStudentRatingPerformance(
                schoolStore.school,
                schoolStore.course,
                student.value,
            ),
        );
    }

    if (performanceStore.studyQuestions.length === 0) {
        performanceStore.setStudyQuestions(
            await CoursePerformanceRepository.getStudentStudyQuizzes(
                schoolStore.school,
                schoolStore.course,
                student.value,
            ),
        );
    }

    examLoaders.value = {
        [TestType.Final]: performanceStore.getFinalQuizPerformanceByTestSessionId,
        [TestType.Optional]: performanceStore.getOptionalQuizPerformanceByTestSessionId,
        [TestType.Practice]: performanceStore.getPracticeTestPerformanceByTestSessionId,
        [TestType.Stage]: performanceStore.getStageTestPerformanceByTestSessionId,
        [TestType.Eoc]: performanceStore.getEocTestPerformanceByTestSessionId,
        [TestType.Study]: performanceStore.getStudyQuestionsPerformanceByStudyUnit,
    };

    if (props.testType === TestType.Study) {
        await loadStudyQuestions();
    } else {
        await loadExamQuestions();
    }

    /** If there are no incorrect questions, clear all filters */
    if (filteredQuestions.value.length === 0) {
        selectedFilters.value = [];
    }

    studyUnitNumber.value = getStudyUnitNumber(exam.value);

    updateTopSectionHeight();
    updateUsableScreenHeight();

    loading.value = false;

    // select the first question when the component is mounted
    pagination.value.set(1);

    $q.loading.hide();
});

onUnmounted(() => {
    $q.loading.hide();
});
</script>

<template>
    <div
        class="container row test-review-container"
        :class="$q.screen.gt.sm ? 'q-px-xl' : 'q-px-sm'"
    >
        <div class="col-12 back-button">
            <q-btn
                v-if="$q.screen.gt.xs"
                class="quiz-button fsum-button-dark-blue q-my-lg icon-left"
                label="Back to Student Record"
                type="button"
                icon="chevron_left"
                no-caps
                flat
                @click="$router.push({ name: 'studentRecord', params: { id: cusId } })"
            />
        </div>
        <div class="col-12">
            <q-layout
                view="hHh lpR fFf"
                container
                class="shadow-2 rounded-borders"
                :style="`height: ${ usableHeight }`"
                @resize="updateUsableScreenHeight()"
            >
                <q-header
                    ref="qHeaderRef"
                    class="bg-fsum-all-blue text-fsum-dark-blue2 q-px-md q-py-sm z-top"
                >
                    <q-toolbar class="q-px-none">
                        <q-btn
                            v-if="$q.screen.gt.xs"
                            flat
                            round
                            dense
                            icon="menu"
                            class="q-mr-sm"
                            aria-label="Toggle side menu"
                            @click="menuOpen = !menuOpen"
                        />
                        <q-btn
                            v-else
                            flat
                            round
                            dense
                            icon="arrow_back"
                            class="q-mr-sm"
                            aria-label="Back to Student Record button"
                            @click="$router.push({ name: 'studentRecord', params: { id: cusId } })"
                        />
                        <q-toolbar-title>{{ student?.firstName }} {{ student?.lastName }}</q-toolbar-title>
                        <div
                            v-if="$q.screen.gt.xs"
                            class="row"
                        >
                            <div
                                v-if="studyUnitNumber !== null"
                                class="column su-text q-pr-lg"
                            >
                                <span class="text-weight-bold">Study Unit</span>
                                <span>{{ studyUnitNumber }}</span>
                            </div>
                            <div class="column su-text">
                                <span class="text-weight-bold">Exam Type</span>
                                <span>{{ testTitle }}</span>
                            </div>
                        </div>
                    </q-toolbar>
                    <q-toolbar
                        v-if="exam !== null && $q.screen.xs"
                        class="justify-between no-padding"
                    >
                        <div class="q-gutter-x-sm row inline header-buttons">
                            <q-btn
                                class="performance-button outline-gray"
                                color="black"
                                icon="bar_chart"
                                dense
                                flat
                                size="12px"
                                square
                                aria-label="Open performance menu"
                                tabindex="0"
                                @click="openPerformanceDialog"
                            />
                            <QuestionFilterButton
                                :submitted-filters="selectedFilters"
                                @click="isFilterDialogOpen = true"
                            />
                            <QuestionFilterDialog
                                v-model="isFilterDialogOpen"
                                :submitted-filters="selectedFilters"
                                @apply-filters="(filters: string[]) => selectedFilters = filters"
                            />
                        </div>
                        <q-btn
                            flat
                            square
                            no-caps
                            dense
                            icon-right="keyboard_arrow_down"
                            class="bg-white q-pl-sm question-list-button outline-gray"
                            aria-label="Open question list"
                            @click="openQuestionListDialog"
                        >
                            <span v-if="filteredQuestions.length > 0">
                                Question {{ filteredQuestions[pagination.page - 1].number }}
                                <TestReviewStatusIcon
                                    :label="getPrimaryQuestionStatus(filteredQuestions[pagination.page - 1])"
                                    :class="'q-pl-sm'"
                                    :size="'20px'"
                                />
                            </span>
                            <span v-else>
                                No results
                            </span>
                        </q-btn>
                    </q-toolbar>
                </q-header>
                <q-drawer
                    v-if="$q.screen.gt.xs"
                    v-model="menuOpen"
                    show-if-above
                    side="left"
                    bordered
                    :breakpoint="600"
                >
                    <q-scroll-area
                        ref="scrollAreaRef"
                        :style="`height: calc(100% - ${ topSectionHeight }px); margin-top: ${ topSectionHeight }px`"
                    >
                        <TestQuestionList
                            v-if="exam !== null"
                            :questions="filteredQuestions"
                        />
                    </q-scroll-area>
                    <div
                        v-if="exam !== null"
                        class="absolute-top"
                        :style="`height: ${ topSectionHeight }px`"
                    >
                        <TestReviewPerformance
                            ref="performanceRef"
                            :questions="questions"
                            :test-type="props.testType"
                            :exam="exam"
                            :school="schoolStore.school"
                            :course="schoolStore.course"
                            :student="student"
                            :is-visible="isPerformanceExpanded"
                            @update:visibility="(value: boolean) => updatePerformanceVisibility(value)"
                            @transition:end="updateTopSectionHeight"
                            @transition:start="updateTopSectionHeight"
                        />
                        <div class="row justify-between">
                            <QuestionFilterMenu
                                ref="filterRef"
                                :class="'q-pl-md q-py-md'"
                                :submitted-filters="selectedFilters"
                                @apply-filters="(filters: string[]) => selectedFilters = filters"
                            />
                            <div class="num-questions q-pr-md">
                                <span>{{ filteredQuestions.length }} results shown</span>
                            </div>
                        </div>
                    </div>
                </q-drawer>
                <q-footer class="bg-fsum-all-blue text-fsum-dark-blue2 q-px-md q-py-sm">
                    <q-toolbar class="justify-between q-px-none">
                        <div>
                            <q-btn
                                v-show="!pagination.isFirstPage && filteredQuestions.length > 1"
                                id="back-btn"
                                label="Previous"
                                type="button"
                                class="fsum-button-outlined quiz-button icon-left"
                                icon="chevron_left"
                                no-caps
                                flat
                                @click="pagination.prev(); updateScrollPosition()"
                            />
                        </div>
                        <div>
                            <q-btn
                                v-show="!pagination.isLastPage && filteredQuestions.length > 1"
                                id="front-btn"
                                label="Next"
                                type="button"
                                class="fsum-button-dark-blue quiz-button"
                                icon-right="chevron_right"
                                no-caps
                                flat
                                @click="pagination.next(); updateScrollPosition()"
                            />
                        </div>
                    </q-toolbar>
                </q-footer>
                <q-page-container>
                    <q-page
                        class="bg-white"
                        :style-fn="getPageHeight"
                    >
                        <div
                            v-if="!loading && filteredQuestions.length === 0"
                            class="text-badge-gray content-center text-center text-h6"
                            style="height: inherit"
                        >
                            No questions available
                        </div>
                        <q-scroll-area
                            v-else
                            tabindex="0"
                            role="region"
                            style="height: inherit"
                        >
                            <div
                                v-if="!loading"
                                class="row"
                            >
                                <div class="col-12">
                                    <TestQuestion
                                        :questions="filteredQuestions"
                                    />
                                </div>
                            </div>
                        </q-scroll-area>
                    </q-page>
                </q-page-container>
            </q-layout>
        </div>
    </div>
</template>

<style scoped lang="scss">
@media screen and (max-width: $breakpoint-xs-max) {
    .back-button {
        padding-top: 1em;
    }
}
.quiz-button {
    &.icon-left {
        padding-left: 5px;
    }

    :deep(.q-icon.on-left) {
        margin-right: 0;
    }

    :deep(.q-icon.on-right) {
        margin-left: 0;
    }
}
.num-questions {
    font-size: 12px;
    color: var(--fsum-med-gray);
    align-content: center;
}
:deep(.header-buttons .q-btn .q-icon) {
    font-size: 22px;
}
:deep(.q-drawer-container aside.q-drawer.q-drawer--mobile) {
    margin-top: v-bind(computedHeaderHeight);
}
</style>
